import { Box, Chip, Grid, IconButton, Stack, Typography } from '@mui/material'
import { C1Button } from 'c1ui'
import { DeleteConfirmationDialogWithTextInput } from 'components/core/confirmation/dialog/delete'
import { C1CopyText } from 'components/core/copytext/C1CopyText'
import { C1TableGrid } from 'components/core/table-grid/C1TableGrid'
import { TextField } from 'components/core/textfield'
import { RelativeTimestamp } from 'components/core/time/display'
import { SPOwnersTable } from 'components/pages/user/details/SPOwnersTable'
import { Check, Pencil, X } from 'lucide-react'
import { ServicePrincipalServiceUpdateRequest } from 'pbts/c1/api/service_principal/v1/service_principal'
import type { User } from 'pbts/c1/api/user/v1/user'
import { UpdateUserRequest, UserStatus } from 'pbts/c1/api/user/v1/user'
import { FieldMask } from 'pbts/google/protobuf/field_mask'
import { FC, useCallback, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { updateServicePrincipalApi } from 'redux_toolkit/servicePrincipals'

type SPDetailProps = {
    user: User
    isEditable: boolean
    onUpdateUser: (req: UpdateUserRequest) => void
}

export const SPDetail: FC<SPDetailProps> = ({ user, isEditable, onUpdateUser }) => {
    const intl = useIntl()
    const { mutate: updateSP } = updateServicePrincipalApi()
    const [showDisableConfirm, setShowDisableConfirm] = useState(false)

    // Inline name editing state
    const [isEditingName, setIsEditingName] = useState(false)
    const [editName, setEditName] = useState(user.displayName)

    const isEnabled = user.status === UserStatus.ENABLED

    const handleNameSave = useCallback(async (): Promise<void> => {
        const trimmed = editName.trim()
        if (!trimmed || trimmed === user.displayName) {
            setIsEditingName(false)
            setEditName(user.displayName)
            return
        }
        await updateSP(ServicePrincipalServiceUpdateRequest.create({
            servicePrincipal: { id: user.id, displayName: trimmed },
            updateMask: FieldMask.create({ paths: ['display_name'] }),
        }))
        setIsEditingName(false)
    }, [editName, user.displayName, user.id, updateSP])

    const handleNameCancel = useCallback((): void => {
        setEditName(user.displayName)
        setIsEditingName(false)
    }, [user.displayName])

    const handleToggleStatus = useCallback((): void => {
        const status = isEnabled ? UserStatus.DISABLED : UserStatus.ENABLED
        onUpdateUser(UpdateUserRequest.create({
            user: { ...user, status },
            updateMask: FieldMask.create({ paths: ['status'] }),
            expandMask: { paths: ['*'] },
        }))
        setShowDisableConfirm(false)
    }, [isEnabled, user, onUpdateUser])

    const handleDisableClick = useCallback((): void => {
        if (isEnabled) {
            setShowDisableConfirm(true)
        } else {
            handleToggleStatus()
        }
    }, [isEnabled, handleToggleStatus])

    return (
        <Stack spacing={3}>
            {/* Service principal details + status in a single card */}
            <C1TableGrid
                title={<FormattedMessage id="sp_detail_title" defaultMessage="Service principal details" />}
            >
                <Grid container spacing={3}>
                    <Grid size={{ xs: 12, sm: 7 }}>
                        <Stack spacing={2.5}>
                            <Box>
                                <Typography variant="subtitle2" color="text.secondary" sx={{ pb: 0.5 }}>
                                    <FormattedMessage id="sp_detail_display_name" defaultMessage="Display name" />
                                </Typography>
                                {isEditingName ? (
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <TextField
                                            value={editName}
                                            onChange={(e): void => setEditName(e.target.value)}
                                            size="small"
                                            fullWidth
                                            autoFocus
                                            label=""
                                            onReturn={(): void => { void handleNameSave() }}
                                            onCancel={handleNameCancel}
                                        />
                                        <IconButton size="small" onClick={(): void => { void handleNameSave() }}>
                                            <Check size={16} />
                                        </IconButton>
                                        <IconButton size="small" onClick={handleNameCancel}>
                                            <X size={16} />
                                        </IconButton>
                                    </Stack>
                                ) : (
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <Typography>{user.displayName}</Typography>
                                        {isEditable && (
                                            <IconButton size="small" onClick={(): void => { setEditName(user.displayName); setIsEditingName(true) }}>
                                                <Pencil size={14} />
                                            </IconButton>
                                        )}
                                    </Stack>
                                )}
                            </Box>

                            <Box>
                                <Typography variant="subtitle2" color="text.secondary" sx={{ pb: 0.5 }}>
                                    <FormattedMessage id="sp_detail_email" defaultMessage="Client email" />
                                </Typography>
                                <C1CopyText value={user.email} typographyVariant="monospace" />
                            </Box>

                            <Grid container spacing={2}>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <Typography variant="subtitle2" color="text.secondary" sx={{ pb: 0.5 }}>
                                        <FormattedMessage id="sp_detail_created" defaultMessage="Created" />
                                    </Typography>
                                    <RelativeTimestamp date={user.createdAt} />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <Typography variant="subtitle2" color="text.secondary" sx={{ pb: 0.5 }}>
                                        <FormattedMessage id="sp_detail_updated" defaultMessage="Last updated" />
                                    </Typography>
                                    <RelativeTimestamp date={user.updatedAt} />
                                </Grid>
                            </Grid>
                        </Stack>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 5 }}>
                        <Stack spacing={2} alignItems="flex-start">
                            <Typography variant="subtitle2" color="text.secondary">
                                <FormattedMessage id="sp_status_title" defaultMessage="Status" />
                            </Typography>
                            <Chip
                                label={isEnabled
                                    ? intl.formatMessage({ id: 'sp_status_enabled', defaultMessage: 'Enabled' })
                                    : intl.formatMessage({ id: 'sp_status_disabled', defaultMessage: 'Disabled' })
                                }
                                color={isEnabled ? 'success' : 'error'}
                                size="small"
                            />
                            <Typography variant="body2" color="text.secondary">
                                {isEnabled
                                    ? <FormattedMessage
                                        id="sp_status_enabled_description"
                                        defaultMessage="Disabling this service principal will immediately prevent all API access via its credentials and federation trusts."
                                    />
                                    : <FormattedMessage
                                        id="sp_status_disabled_description"
                                        defaultMessage="This service principal is disabled. All credentials and federation trusts are inactive. Re-enable to restore API access."
                                    />
                                }
                            </Typography>
                            {isEditable && (
                                <C1Button
                                    variant="outlined"
                                    color={isEnabled ? 'error' : 'success'}
                                    onClick={handleDisableClick}
                                >
                                    {isEnabled
                                        ? <FormattedMessage id="sp_status_disable_button" defaultMessage="Disable service principal" />
                                        : <FormattedMessage id="sp_status_enable_button" defaultMessage="Enable service principal" />
                                    }
                                </C1Button>
                            )}
                        </Stack>
                    </Grid>
                </Grid>
            </C1TableGrid>

            {/* Owners section: full width below */}
            <SPOwnersTable userId={user.id} isEditable={isEditable} />

            {/* Disable confirmation dialog */}
            {showDisableConfirm && (
                <DeleteConfirmationDialogWithTextInput
                    open={showDisableConfirm}
                    itemName={user.displayName}
                    title={intl.formatMessage(
                        { id: 'sp_disable_confirm_title', defaultMessage: 'Disable {name}' },
                        { name: user.displayName },
                    )}
                    action={intl.formatMessage({ id: 'sp_disable_confirm_action', defaultMessage: 'Disable' })}
                    details={
                        <FormattedMessage
                            id="sp_disable_confirm_details"
                            defaultMessage="All credentials and federation trusts for this service principal will immediately stop working. This can be reversed by re-enabling."
                        />
                    }
                    onConfirm={handleToggleStatus}
                    onCancel={(): void => setShowDisableConfirm(false)}
                />
            )}
        </Stack>
    )
}
